import { useMemo } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import { useFixedPhoneSlice } from "app/pages/Customer/FixedPhone/slice";
import type { FixedPhoneDashboardData } from "app/pages/Customer/FixedPhone/slice/types";

const isFetchBaseQueryError = (
  error: unknown
): error is FetchBaseQueryError => {
  if (!error || typeof error !== "object") {
    return false;
  }

  return "status" in error;
};

const getErrorMessageFromFetchError = (error: FetchBaseQueryError) => {
  const { data, status } = error;

  if (typeof data === "string") {
    return data;
  }

  if (data && typeof data === "object") {
    const singleMessage = (data as { message?: string })?.message;
    if (singleMessage) {
      return singleMessage;
    }

    const messages = (data as { messages?: string[] })?.messages;
    if (Array.isArray(messages) && messages.length > 0) {
      return messages[0];
    }
  }

  return `Request failed with status ${status}.`;
};

const isSerializedErrorWithMessage = (
  error: unknown
): error is SerializedError & { message: string } => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const serializedError = error as SerializedError;
  return typeof serializedError.message === "string";
};

const getDefaultErrorMessage = () =>
  "Unable to load your fixed phone dashboard data right now.";

export const useFixedPhoneDashboard = (customerId: string | undefined) => {
  const { useLazyGetFixedPhoneDashboardQuery } = useFixedPhoneSlice();

  const [
    getFixedPhoneDashboard,
    { data, isLoading, isFetching, isError, error },
  ] = useLazyGetFixedPhoneDashboardQuery();

  const liveData = data?.data;
  const hasLiveData = Boolean(liveData);

  const dashboardData = useMemo<FixedPhoneDashboardData | null>(() => {
    if (!liveData) {
      return null;
    }

    return liveData;
  }, [liveData]);

  const errorMessage = useMemo(() => {
    if (isFetchBaseQueryError(error)) {
      return getErrorMessageFromFetchError(error);
    }

    if (isSerializedErrorWithMessage(error)) {
      return error.message;
    }

    if (!hasLiveData && !isLoading && !isFetching) {
      return getDefaultErrorMessage();
    }

    return getDefaultErrorMessage();
  }, [error, hasLiveData, isLoading, isFetching]);

  const shouldShowError =
    isError || (!hasLiveData && !isLoading && !isFetching);

  return {
    dashboardData,
    isLoading,
    isFetching,
    isError: shouldShowError,
    errorMessage,
    getFixedPhoneDashboard,
  };
};
